import { ButtonBase, FormControl, FormLabel, Stack, TextField, Typography } from '@mui/material';
import { AddressAutocomplete } from 'components/AddressAutocomplete/AddressAutocomplete';
import { useNotificationMessages } from 'hooks/useNotificationMessages';
import { getErrorMessage } from 'utils/errors';
import { useStatelessPost } from 'hooks/useStatelessPost';
import { useCallback, useEffect, useState } from 'react';
import { FieldError } from 'react-hook-form';
import { ControllerRenderProps } from 'react-hook-form/dist/types/controller';
import { useDebouncedCallback } from 'use-debounce';

export interface AddressValue {
  address: string;
  city: string;
  country: string;
  stateOrProvince: string;
  postalCode: string;
}

interface PlacesAutocompleteFieldProps {
  label: string;
  disabled: boolean;
  value: AddressValue;
  onChange: ControllerRenderProps['onChange'];
  error?: FieldError;
  name: string;
  readOnly: boolean;
  restrictToCountry?: string;
  formatWithUSPS?: boolean;
}

const stateToAbbreviationMap = {
  Alabama: 'AL',
  Alaska: 'AK',
  Arizona: 'AZ',
  Arkansas: 'AR',
  California: 'CA',
  Colorado: 'CO',
  Connecticut: 'CT',
  Delaware: 'DE',
  Florida: 'FL',
  Georgia: 'GA',
  Hawaii: 'HI',
  Idaho: 'ID',
  Illinois: 'IL',
  Indiana: 'IN',
  Iowa: 'IA',
  Kansas: 'KS',
  Kentucky: 'KY',
  Louisiana: 'LA',
  Maine: 'ME',
  Maryland: 'MD',
  Massachusetts: 'MA',
  Michigan: 'MI',
  Minnesota: 'MN',
  Mississippi: 'MS',
  Missouri: 'MO',
  Montana: 'MT',
  Nebraska: 'NE',
  Nevada: 'NV',
  'New Hampshire': 'NH',
  'New Jersey': 'NJ',
  'New Mexico': 'NM',
  'New York': 'NY',
  'North Carolina': 'NC',
  'North Dakota': 'ND',
  Ohio: 'OH',
  Oklahoma: 'OK',
  Oregon: 'OR',
  Pennsylvania: 'PA',
  'Rhode Island': 'RI',
  'South Carolina': 'SC',
  'South Dakota': 'SD',
  Tennessee: 'TN',
  Texas: 'TX',
  Utah: 'UT',
  Vermont: 'VT',
  Virginia: 'VA',
  Washington: 'WA',
  'West Virginia': 'WV',
  Wisconsin: 'WI',
  Wyoming: 'WY',
};

export const getStateAbbreviation = (stateName: string): string | undefined => {
  if (Object.values(stateToAbbreviationMap).includes(stateName.trim())) {
    return stateName.trim();
  }
  return stateToAbbreviationMap[stateName.trim()] ?? undefined;
};

type USPSResponse = {
  error?: {
    message: string;
  };
  address: {
    streetAddress: string;
    city: string;
    state: string;
    ZIPCode: string;
    country: string;
  };
};

export const PlacesAutocompleteField = ({
  label,
  disabled,
  value,
  onChange,
  error,
  name,
  readOnly,
  restrictToCountry,
  formatWithUSPS,
}: PlacesAutocompleteFieldProps) => {
  const [showAddressFields, setShowAddressFields] = useState(false);
  const toggleLinkText = showAddressFields ? 'Search for address instead' : "Can't find your address? Enter it manually";
  const postAddress = useStatelessPost<USPSResponse>('/usps/address/details');
  const { showErrorMessage, showSuccessMessage } = useNotificationMessages();
  const shouldValidateWithUSPS = restrictToCountry === 'us' && formatWithUSPS;

  const validateAddress = useCallback(
    (address: AddressValue) => {
      if (address.address && address.city && address.stateOrProvince && address.postalCode && address.country) {
        postAddress({
          streetAddress: address.address,
          city: address.city,
          state: getStateAbbreviation(address.stateOrProvince),
          postalCode: address.postalCode,
          country: address.country,
        })
          .then((data) => {
            if (data.error) {
              showErrorMessage(getErrorMessage(data.error));
            } else {
              // After validating the address, update the field's value to match USPS' result.
              onChange({
                address: data.address.streetAddress,
                city: data.address.city,
                stateOrProvince: data.address.state,
                postalCode: data.address.ZIPCode,
                country: 'United States',
              });
              showSuccessMessage('Address validated successfully');
            }
          })
          .catch((error) => {
            showErrorMessage(getErrorMessage(error));
          });
      }
    },
    [postAddress, showErrorMessage, onChange, showSuccessMessage],
  );

  const debounceValidateAddress = useDebouncedCallback((address: AddressValue) => {
    validateAddress(address);
  }, 500);

  const handleInputChange = (key, val) => {
    const newValue = value || {};
    newValue[key] = val;
    onChange(newValue);
    if (shouldValidateWithUSPS) debounceValidateAddress(newValue);
  };

  const onChangeWithValidation = (value: AddressValue) => {
    onChange(value);
    if (shouldValidateWithUSPS) debounceValidateAddress(value);
  };

  const addressInputs = [
    { name: 'address', label: 'Address' },
    { name: 'city', label: 'Current City' },
    { name: 'country', label: 'Current Country' },
    { name: 'stateOrProvince', label: 'Current State/Province' },
    { name: 'postalCode', label: 'Current Postal Code' },
  ];

  useEffect(() => {
    // If the user has added their address, we need to show the address fields by default
    if (value || readOnly) {
      setShowAddressFields(true);
    }
  }, [value, readOnly]);

  return (
    <>
      <Stack aria-labelledby={`${name}-label`} direction="row" alignItems="center" gap={3}>
        <FormControl fullWidth size="small" data-cy-field-type="placesautocomplete" sx={{ flexBasis: '33%' }}>
          <FormLabel id={`${name}-label`}>{label}</FormLabel>
          <AddressAutocomplete
            restrictToCountry={restrictToCountry}
            onChange={({ addressLine1: address, city, state: stateOrProvince, country, postCode: postalCode }) => {
              const newValue = { address, city, stateOrProvince, country, postalCode };
              shouldValidateWithUSPS ? onChangeWithValidation(newValue) : onChange(newValue);
            }}
            disabled={disabled || readOnly}
          />
        </FormControl>
        <ButtonBase sx={{ color: (theme) => theme.palette.primary.main, mt: 2 }} onClick={() => setShowAddressFields(!showAddressFields)}>
          {toggleLinkText}
        </ButtonBase>
      </Stack>
      {showAddressFields && (
        <Stack flexWrap="wrap" direction="row" gap={3} justifyContent="space-between" mt={2}>
          {addressInputs.map(({ name: inputName, label: inputLabel }) => (
            <FormControl key={inputName} fullWidth size="small" sx={{ flexBasis: (theme) => `calc(50% - ${theme.spacing(2)})` }}>
              <FormLabel id={`${name}-${inputName}-label`}>{inputLabel}</FormLabel>
              <TextField
                size="small"
                aria-labelledby={`${name}-${inputName}-label`}
                value={value[inputName]}
                onChange={(event) => handleInputChange(inputName, event.target.value)}
                inputProps={{ readOnly }}
                disabled={disabled}
              />
            </FormControl>
          ))}
        </Stack>
      )}
      {error ? <Typography color="error">{error!.message as string}</Typography> : <br />}
    </>
  );
};

// for React.lazy
export default PlacesAutocompleteField;
